import { z } from 'zod';
/**
 * Vision & Lighting System
 *
 * Handles D&D 5e vision rules:
 * - Normal vision (requires light)
 * - Darkvision (see in darkness as dim light)
 * - Blindsight (see without relying on sight)
 * - Truesight (see through illusions, invisibility)
 * - Tremorsense (detect vibrations)
 *
 * And light sources:
 * - Bright light (normal vision)
 * - Dim light (disadvantage on Perception)
 * - Darkness (heavily obscured)
 */
// =============================================================================
// VISION TYPES
// =============================================================================
/**
 * Special vision abilities per D&D 5e rules
 */
export const VisionTypeSchema = z.enum([
    'normal', // Standard human vision - needs light
    'darkvision', // See in darkness as dim light (most common)
    'blindsight', // Perceive surroundings without sight
    'truesight', // See through magical darkness, invisibility
    'tremorsense', // Detect creatures touching the ground
]);
/**
 * Vision configuration for a creature/character
 */
export const VisionConfigSchema = z.object({
    // Basic vision range in feet (used for fog of war)
    normalRange: z.number().default(60),
    // Darkvision range in feet (0 if no darkvision)
    darkvisionRange: z.number().default(0),
    // Blindsight range in feet (0 if no blindsight)
    blindsightRange: z.number().default(0),
    // Truesight range in feet (0 if no truesight)
    truesightRange: z.number().default(0),
    // Tremorsense range in feet (0 if no tremorsense)
    tremorsenseRange: z.number().default(0),
    // Can see in magical darkness? (requires Devil's Sight or similar)
    seeMagicalDarkness: z.boolean().default(false),
    // Can see invisible creatures?
    seeInvisible: z.boolean().default(false),
});
// =============================================================================
// LIGHT TYPES
// =============================================================================
/**
 * Light level categories per D&D 5e
 */
export const LightLevelSchema = z.enum([
    'bright', // Normal vision, no penalties
    'dim', // Lightly obscured - disadvantage on Perception
    'darkness', // Heavily obscured - effectively blinded
]);
/**
 * Light source type (determines behavior)
 */
export const LightSourceTypeSchema = z.enum([
    'natural', // Sunlight, moonlight - affected by time of day
    'fire', // Torch, lantern, campfire - can be extinguished
    'magical', // Light spell, dancing lights - has duration
    'darkness', // Darkness spell - blocks non-magical light
]);
/**
 * Preset light sources for quick creation
 */
export const LightPresetSchema = z.enum([
    'torch', // 20ft bright, 20ft dim
    'lantern', // 30ft bright, 30ft dim (hooded)
    'bullseye', // 60ft cone bright, 60ft dim
    'candle', // 5ft bright, 5ft dim
    'campfire', // 20ft bright, 40ft dim
    'light_cantrip', // 20ft bright, 20ft dim
    'daylight_spell', // 60ft bright, 60ft dim
    'darkness_spell', // 15ft radius magical darkness
    'dancing_lights', // 10ft dim each (4 lights)
    'faerie_fire', // No light, outlines targets
    'moonlight', // Dim light (nighttime outdoors)
    'starlight', // Dim light (clear night)
    'overcast', // Dim light (cloudy day)
]);
/**
 * Light source configuration
 */
export const LightSourceSchema = z.object({
    id: z.string(),
    // Position (can be attached to token or placed on map)
    position: z.object({ x: z.number(), y: z.number() }),
    attachedToTokenId: z.string().optional(),
    // Light properties
    type: LightSourceTypeSchema,
    preset: LightPresetSchema.optional(),
    // Custom ranges (override preset)
    brightRadius: z.number(), // Feet of bright light
    dimRadius: z.number(), // Additional feet of dim light beyond bright
    // Visual properties
    color: z.string().default('#ffcc00'), // Warm yellow default
    intensity: z.number().min(0).max(1).default(1),
    // For cone lights (bullseye lantern)
    angle: z.number().optional(), // Cone angle in degrees
    direction: z.number().optional(), // Direction facing in degrees
    // Magical properties
    isMagical: z.boolean().default(false),
    blocksMagicalDarkness: z.boolean().default(false), // True for Daylight spell
    duration: z.number().optional(), // Seconds remaining (for spells)
    // State
    enabled: z.boolean().default(true),
    visible: z.boolean().default(true), // Show to players?
    createdAt: z.date().default(() => new Date()),
});
// =============================================================================
// PRESETS
// =============================================================================
/**
 * Light preset configurations
 */
export const LIGHT_PRESETS = {
    torch: { bright: 20, dim: 20, color: '#ff9933', type: 'fire' },
    lantern: { bright: 30, dim: 30, color: '#ffcc66', type: 'fire' },
    bullseye: { bright: 60, dim: 60, color: '#ffcc66', type: 'fire' },
    candle: { bright: 5, dim: 5, color: '#ffcc99', type: 'fire' },
    campfire: { bright: 20, dim: 40, color: '#ff6633', type: 'fire' },
    light_cantrip: { bright: 20, dim: 20, color: '#ffffff', type: 'magical' },
    daylight_spell: { bright: 60, dim: 60, color: '#ffffee', type: 'magical' },
    darkness_spell: { bright: 0, dim: 0, color: '#000000', type: 'darkness' },
    dancing_lights: { bright: 0, dim: 10, color: '#aaccff', type: 'magical' },
    faerie_fire: { bright: 0, dim: 0, color: '#ff66ff', type: 'magical' },
    moonlight: { bright: 0, dim: 60, color: '#ccddff', type: 'natural' },
    starlight: { bright: 0, dim: 30, color: '#aabbcc', type: 'natural' },
    overcast: { bright: 0, dim: 120, color: '#aaaaaa', type: 'natural' },
};
/**
 * Common vision presets for creatures
 */
export const VISION_PRESETS = {
    human: {
        normalRange: 60,
        darkvisionRange: 0,
        blindsightRange: 0,
        truesightRange: 0,
        tremorsenseRange: 0,
        seeMagicalDarkness: false,
        seeInvisible: false,
    },
    elf: {
        normalRange: 60,
        darkvisionRange: 60,
        blindsightRange: 0,
        truesightRange: 0,
        tremorsenseRange: 0,
        seeMagicalDarkness: false,
        seeInvisible: false,
    },
    dwarf: {
        normalRange: 60,
        darkvisionRange: 60,
        blindsightRange: 0,
        truesightRange: 0,
        tremorsenseRange: 0,
        seeMagicalDarkness: false,
        seeInvisible: false,
    },
    drow: {
        normalRange: 60,
        darkvisionRange: 120,
        blindsightRange: 0,
        truesightRange: 0,
        tremorsenseRange: 0,
        seeMagicalDarkness: false,
        seeInvisible: false,
    },
    devil_sight: {
        normalRange: 60,
        darkvisionRange: 120,
        blindsightRange: 0,
        truesightRange: 0,
        tremorsenseRange: 0,
        seeMagicalDarkness: true,
        seeInvisible: false,
    },
    bat: {
        normalRange: 60,
        darkvisionRange: 60,
        blindsightRange: 60,
        truesightRange: 0,
        tremorsenseRange: 0,
        seeMagicalDarkness: false,
        seeInvisible: false,
    },
    true_seeing: {
        normalRange: 120,
        darkvisionRange: 120,
        blindsightRange: 0,
        truesightRange: 120,
        tremorsenseRange: 0,
        seeMagicalDarkness: true,
        seeInvisible: true,
    },
    tremorsense_creature: {
        normalRange: 60,
        darkvisionRange: 0,
        blindsightRange: 0,
        truesightRange: 0,
        tremorsenseRange: 60,
        seeMagicalDarkness: false,
        seeInvisible: false,
    },
};
// =============================================================================
// GLOBAL LIGHTING
// =============================================================================
/**
 * Scene-wide lighting configuration
 */
export const GlobalLightingSchema = z.object({
    // Ambient light level for the entire scene
    ambientLevel: LightLevelSchema.default('bright'),
    // Color tint for ambient light
    ambientColor: z.string().default('#ffffff'),
    // Ambient intensity (0-1)
    ambientIntensity: z.number().min(0).max(1).default(1),
    // Is it daytime? (affects natural light sources)
    isDaytime: z.boolean().default(true),
    // Is the scene underground/indoors? (no natural light)
    isIndoors: z.boolean().default(false),
    // Fog of war settings
    fogOfWar: z.object({
        enabled: z.boolean().default(true),
        exploredAlpha: z.number().min(0).max(1).default(0.5), // How visible explored areas are
        unexploredAlpha: z.number().min(0).max(1).default(0.97), // How dark unexplored areas are
    }).default({}),
});
// =============================================================================
// HELPER FUNCTIONS
// =============================================================================
/**
 * Create a light source from a preset
 */
export function createLightFromPreset(preset, position, attachedToTokenId) {
    const config = LIGHT_PRESETS[preset];
    return {
        position,
        attachedToTokenId,
        type: config.type,
        preset,
        brightRadius: config.bright,
        dimRadius: config.dim,
        color: config.color,
        intensity: 1,
        isMagical: config.type === 'magical' || config.type === 'darkness',
        blocksMagicalDarkness: preset === 'daylight_spell',
        enabled: true,
        visible: true,
    };
}
/**
 * Calculate effective vision range for a creature at a given light level
 */
export function getEffectiveVisionRange(vision, lightLevel, hasMagicalDarkness = false) {
    // In magical darkness
    if (hasMagicalDarkness) {
        if (vision.seeMagicalDarkness) {
            return vision.darkvisionRange || vision.normalRange;
        }
        // Blindsight/truesight/tremorsense still work
        return Math.max(vision.blindsightRange, vision.truesightRange, vision.tremorsenseRange);
    }
    // Normal calculations based on light level
    switch (lightLevel) {
        case 'bright':
            return vision.normalRange;
        case 'dim':
            // Darkvision treats dim as bright
            if (vision.darkvisionRange > 0) {
                return Math.max(vision.normalRange, vision.darkvisionRange);
            }
            // Normal vision in dim light - halved range
            return vision.normalRange / 2;
        case 'darkness':
            // Can see with darkvision (as dim light)
            if (vision.darkvisionRange > 0) {
                return vision.darkvisionRange;
            }
            // Blindsight/truesight/tremorsense work in darkness
            return Math.max(vision.blindsightRange, vision.truesightRange, vision.tremorsenseRange);
    }
}
/**
 * Determine light level at a point given light sources
 */
export function getLightLevelAtPoint(point, lightSources, globalLighting, feetPerSquare = 5) {
    // Start with ambient level
    let currentLevel = globalLighting.ambientLevel;
    let hasMagicalDarkness = false;
    // Check each light source
    for (const light of lightSources) {
        if (!light.enabled)
            continue;
        // Calculate distance in feet
        const dx = point.x - light.position.x;
        const dy = point.y - light.position.y;
        const distancePixels = Math.sqrt(dx * dx + dy * dy);
        const distanceFeet = (distancePixels / 50) * feetPerSquare; // Assuming 50px grid
        // Darkness spell overrides other lights (unless blocked by Daylight)
        if (light.type === 'darkness') {
            const darknessRadius = 15; // Darkness spell is 15ft radius
            if (distanceFeet <= darknessRadius) {
                hasMagicalDarkness = true;
                // Check if any Daylight spell blocks this
                const daylight = lightSources.find((l) => l.enabled && l.blocksMagicalDarkness);
                if (!daylight) {
                    currentLevel = 'darkness';
                }
            }
            continue;
        }
        // Check if within light radius
        if (distanceFeet <= light.brightRadius) {
            currentLevel = 'bright';
        }
        else if (distanceFeet <= light.brightRadius + light.dimRadius) {
            if (currentLevel !== 'bright') {
                currentLevel = 'dim';
            }
        }
    }
    return { level: currentLevel, isMagicalDarkness: hasMagicalDarkness };
}
/**
 * Default vision config for new tokens
 */
export const DEFAULT_VISION = {
    normalRange: 60,
    darkvisionRange: 0,
    blindsightRange: 0,
    truesightRange: 0,
    tremorsenseRange: 0,
    seeMagicalDarkness: false,
    seeInvisible: false,
};
/**
 * Default global lighting (bright daylight)
 */
export const DEFAULT_GLOBAL_LIGHTING = {
    ambientLevel: 'bright',
    ambientColor: '#ffffff',
    ambientIntensity: 1,
    isDaytime: true,
    isIndoors: false,
    fogOfWar: {
        enabled: true,
        exploredAlpha: 0.5,
        unexploredAlpha: 0.97,
    },
};
//# sourceMappingURL=vision-types.js.map